import React, { FunctionComponent, PropsWithChildren, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStandard, ButtonStandardType } from 'record-it-components/button/ButtonStandard/ButtonStandard';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { useNotificationManager } from 'record-it-components/hooks/useNotificationManager';
import { Accordion } from 'record-it-components/layout/Accordion/Accordion';
import { LoadingOverlay } from 'record-it-components/misc/LoadingOverlay/LoadingOverlay';
import { BaseDialogTextButton, BaseDialogTextButtonType } from 'record-it-components/modal/BaseDialogTextButton/BaseDialogTextButton';
import { SingleTabBaseDialog, SingleTabBaseDialogProps } from 'record-it-components/modal/SingleTabBaseDialog/SingleTabBaseDialog';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { EntityName } from 'record-it-time-common/EntityName';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { DownloadUtils } from 'record-it-utils/utils/DownloadUtils/DownloadUtils';
import { StringUtils } from 'record-it-utils/utils/StringUtils/StringUtils';
import { ApiAdapter } from '../../../classes/ApiAdapter/ApiAdapter';
import { User } from '../../../classes/entities/User/userTypes';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { useSynchroMemo } from '../../../hooks/entity/useSynchroMemo/useSynchroMemo';
import { allMonths, Month } from '../../input/MonthSelect/Month';
import { allMonthsInYearIncludingAny, allMonthsOption, MonthSelect, MonthSelectOption } from '../../input/MonthSelect/MonthSelect';
import { UserSelect } from '../../input/UserSelect/UserSelect';
import { ExportYearSelect } from '../ExportYearSelect/ExportYearSelect';
import styles from './ExportWorkTimeBaseDialog.module.css';
export type ExportWorkTimeBaseDialogProps = Pick<SingleTabBaseDialogProps, 'opened' | 'onBackdropClick'> & {
  getUserId?: () => string;
  onCancelClick: () => void;
  showLoading?: boolean;
};

/**
 * A basic configurable version of a dialog for exporting work times
 */
export const ExportWorkTimeBaseDialog: FunctionComponent<PropsWithChildren<ExportWorkTimeBaseDialogProps>> = React.memo(props => {
  const {
    children,
    onCancelClick,
    getUserId,
    showLoading,
    ...remainingProps
  } = prepareProps({
    props
  });
  const [loading, setLoading] = useState(false);
  const [csvYear, setCsvYear] = useState<number>(new Date().getFullYear());
  const [csvMonth, setCsvMonth] = useState<MonthSelectOption>(new Date().getMonth() + 1);
  const [csvUserId, setCsvUserId] = useState<string | null>(null);
  const csvExportEnabled = getUserId || !!csvUserId;
  const [timesheetYear, setTimesheetYear] = useState<number>(new Date().getFullYear());
  const [timesheetMonth, setTimesheetMonth] = useState<Month>(new Date().getMonth() + 1);
  const [timesheetUserId, setTimesheetUserId] = useState<string | null>(null);
  const userRepository = useEntityRepository(EntityName.User);
  const timesheetUser = useSynchroMemo(() => {
    return timesheetUserId ? userRepository.getById(timesheetUserId) : null;
  }, [userRepository, timesheetUserId], [EntityName.User]);
  const timesheetExportEnabled = !!timesheetUserId;
  const {
    t
  } = useTranslation();
  const notificationManager = useNotificationManager();
  const renderButtons = useCallback(() => [<BaseDialogTextButton key="cancel" type={BaseDialogTextButtonType.Secondary} data-testid="ExportWorkTimeBaseDialog.CancelButton" onClick={onCancelClick}>
        {t('components.workTime.ExportWorkTimeBaseDialog.cancel')}
      </BaseDialogTextButton>], [t, onCancelClick]);
  const getCsvUserId = (): string | null => {
    return getUserId?.() ?? csvUserId;
  };
  const handleCsvExportClick = useEventCallback(() => {
    setLoading(true);
    const userId = getCsvUserId();
    assertNotNullOrUndefined(userId, 'cannot export csv without a user');
    void ApiAdapter.exportWorkTimeCsv({
      year: csvYear,
      month: csvMonth === allMonthsOption ? null : csvMonth,
      userId
    }).then(response => {
      if (response.success) {
        DownloadUtils.downloadUtf8AsUtf16le(response.csvData, getCsvExportFileName({
          year: csvYear,
          month: csvMonth
        }));
      } else {
        handleExportError();
      }
    }).finally(() => {
      setLoading(false);
    });
  });
  const handleTimesheetExportClick = useEventCallback(() => {
    assertNotNullOrUndefined(timesheetUserId, 'cannot handleTimesheetExport without timesheetUserId');
    assertNotNullOrUndefined(timesheetUser, 'cannot handleTimesheetExport without timesheetUser');
    setLoading(true);
    void ApiAdapter.exportWorkTimeTimesheet({
      daterange: {
        year: timesheetYear,
        month: timesheetMonth
      },
      forUserId: timesheetUserId
    }).then(response_0 => {
      const filename = getTimesheetExportFileName({
        user: timesheetUser,
        year: timesheetYear,
        month: timesheetMonth
      });
      DownloadUtils.download(filename, response_0);
    }).finally(() => {
      setLoading(false);
    });
  });
  const getCsvExportFileName = (filter: YearAndMonth): string => {
    const userId_0 = getCsvUserId();
    assertNotNullOrUndefined(userId_0, 'cannot get csv export file name without a user id ');
    const user = userRepository.getRequiredById(userId_0);
    const year = filter.year.toString();
    if (filter.month !== allMonthsOption) {
      const month = StringUtils.padLeft(filter.month.toString(), 2);
      return t('components.workTime.ExportWorkTimeBaseDialog.exports.csv.fileName.withMonth', {
        year,
        month,
        userName: user.name
      });
    }
    return t('components.workTime.ExportWorkTimeBaseDialog.exports.csv.fileName.withoutMonth', {
      year,
      userName: user.name
    });
  };
  const getTimesheetExportFileName = (filter_0: {
    year: number;
    month: number;
    user: User;
  }): string => {
    const user_0 = filter_0.user.name;
    const year_0 = filter_0.year.toString();
    const month_0 = StringUtils.padLeft(filter_0.month.toString(), 2);
    return t('components.workTime.ExportWorkTimeBaseDialog.exports.timesheet.fileName', {
      user: user_0,
      year: year_0,
      month: month_0
    });
  };
  const handleExportError = (): void => {
    notificationManager.error({
      title: t('components.workTime.ExportWorkTimeBaseDialog.exportError'),
      message: ''
    });
  };
  const [csvExportOpen, setCsvExportOpen] = useState(true);
  const [timesheetExportOpen, setTimesheetExportOpen] = useState(true);
  return <SingleTabBaseDialog title={t('components.workTime.ExportWorkTimeBaseDialog.title')} renderButtons={renderButtons} {...remainingProps}>
      <Accordion summary={t('components.workTime.ExportWorkTimeBaseDialog.exports.csv.title')} open={csvExportOpen} onToggle={setCsvExportOpen}>
        <FormGroup>
          <div className={styles.exportInputForm} data-testid="ExportWorkTimeBaseDialog.CsvExport">
            <ExportYearSelect year={csvYear} onYearSelected={setCsvYear}></ExportYearSelect>
            <MonthSelect month={csvMonth} availableMonths={allMonthsInYearIncludingAny} onMonthSelected={setCsvMonth}></MonthSelect>
            {getUserId ? null : <UserSelect value={csvUserId} onValueChanged={setCsvUserId}></UserSelect>}
          </div>
        </FormGroup>
        <ButtonStandard type={ButtonStandardType.Primary} disabled={!csvExportEnabled} onClick={handleCsvExportClick} data-testid="ExportWorkTimeBaseDialog.Csv.ExportButton">
          {t('components.workTime.ExportWorkTimeBaseDialog.export')}
        </ButtonStandard>
      </Accordion>

      <Accordion summary={t('components.workTime.ExportWorkTimeBaseDialog.exports.timesheet.title')} open={timesheetExportOpen} onToggle={setTimesheetExportOpen}>
        <FormGroup>
          <div className={styles.exportInputForm} data-testid="ExportWorkTimeBaseDialog.TimesheetExport">
            <ExportYearSelect year={timesheetYear} onYearSelected={setTimesheetYear}></ExportYearSelect>
            <MonthSelect month={timesheetMonth} availableMonths={allMonths} onMonthSelected={setTimesheetMonth}></MonthSelect>
            <UserSelect value={timesheetUserId} onValueChanged={setTimesheetUserId}></UserSelect>
          </div>
        </FormGroup>
        <ButtonStandard type={ButtonStandardType.Primary} disabled={!timesheetExportEnabled} onClick={handleTimesheetExportClick} data-testid="ExportWorkTimeBaseDialog.Timesheet.ExportButton">
          {t('components.workTime.ExportWorkTimeBaseDialog.export')}
        </ButtonStandard>
      </Accordion>

      {children}

      <LoadingOverlay active={showLoading || loading} data-testid="ExportWorkTimeBaseDialog.LoadingOverlay"></LoadingOverlay>
    </SingleTabBaseDialog>;
});
type YearAndMonth = {
  year: number;
  month: MonthSelectOption;
};